import React, {useMemo} from 'react';
import {Box, Typography} from '@mui/material';
import {alpha} from '@mui/material/styles';
import {LocalShippingOutlined, StorefrontOutlined} from '@mui/icons-material';
import useDeliveryMode from '../../../hooks/use-delivery-mode';
import {DeliveryModeConst} from '../../../../core/constants/enums';

const deliveryModeStyles = {
    pickupAndDelivery: {
        background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        color: 'primary.contrastText',
        borderColor: (theme) => alpha(theme.palette.primary.light, 0.55),
    },
    pickupOnly: {
        background: (theme) => `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`,
        color: 'warning.contrastText',
        borderColor: (theme) => alpha(theme.palette.warning.light, 0.55),
    },
    deliveryOnly: {
        background: (theme) => `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.info.dark} 100%)`,
        color: 'info.contrastText',
        borderColor: (theme) => alpha(theme.palette.info.light, 0.55),
    },
};

const DeliveryModeIcons = {
    [DeliveryModeConst.PickupAndDelivery]: () => (
        <Box sx={{display: 'inline-flex', alignItems: 'center', gap: 0.25}}>
            <StorefrontOutlined sx={{fontSize: 'inherit'}}/>
            <LocalShippingOutlined sx={{fontSize: 'inherit'}}/>
        </Box>
    ),
    [DeliveryModeConst.PickupOnly]: () => <StorefrontOutlined sx={{fontSize: 'inherit'}}/>,
    [DeliveryModeConst.DeliveryOnly]: () => <LocalShippingOutlined sx={{fontSize: 'inherit'}}/>,
};

const DeliveryModeChip = ({deliveryMode, size = 'medium'}) => {
    const {getLabel, getColorKey, isValidDeliveryMode} = useDeliveryMode();

    const label = useMemo(() => getLabel(deliveryMode), [deliveryMode, getLabel]);
    const colorKey = useMemo(() => getColorKey(deliveryMode), [deliveryMode, getColorKey]);

    if (!isValidDeliveryMode(deliveryMode) || !label || !colorKey)
        return null;

    const isSmall = size === 'small';
    const Icon = DeliveryModeIcons[deliveryMode];

    return (
        <Box
            component={'span'}
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: isSmall ? 0.75 : 1,
                borderRadius: 2,
                px: isSmall ? 1.5 : 2,
                py: isSmall ? 0.625 : 0.875,
                fontWeight: 700,
                lineHeight: 1.2,
                border: '1px solid',
                boxShadow: (theme) => `0 2px 10px ${alpha(theme.palette.common.black, 0.1)}, 0 1px 3px ${alpha(theme.palette.common.black, 0.06)}`,
                ...deliveryModeStyles[colorKey],
            }}
        >
            <Box
                component={'span'}
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: isSmall ? '0.95rem' : '1.1rem',
                    color: 'inherit',
                    opacity: 0.95,
                }}
            >
                <Icon/>
            </Box>
            <Typography
                component={'span'}
                variant={isSmall ? 'caption' : 'body2'}
                fontWeight={700}
                sx={{
                    color: 'inherit',
                    lineHeight: 1.2,
                    letterSpacing: '0.03em',
                    textTransform: 'uppercase',
                    fontSize: isSmall ? '0.7rem' : '0.8rem',
                }}
            >
                {label}
            </Typography>
        </Box>
    );
};

export default DeliveryModeChip;
